/**
 * Yup validation helpers — react-select 가 object {value,label} 또는 string 둘 다 반환
 * 가능한 환경 (모바일 vs 데스크톱) 에서 안전하게 검증.
 *
 * Yup.mixed().required() 만으로는 빈 문자열 ("") 또는 빈 객체 ({}) 도 truthy 로
 * 통과하는 문제가 있어 명시적 not-empty 테스트로 보완.
 */

import * as Yup from "yup";

/**
 * 필수 필드 — string 또는 object 둘 다 받지만 빈 값 거부
 *
 *  string "bri"       → ✓ 통과
 *  object {value:'bri'} → ✓ 통과
 *  null/undefined     → ✗ 실패
 *  ""                 → ✗ 실패
 *  {}                 → ✗ 실패 (value 필드 없음)
 *  {value:''}         → ✗ 실패 (value 가 빈 문자열)
 */
export const requiredField = Yup.mixed()
    .test('not-empty', 'error_valueMissing', (value) => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'object') {
            return value && value.value !== undefined && value.value !== null && value.value !== '';
        }
        if (typeof value === 'string') return value !== '';
        return true;
    })
    .nullable();

/**
 * Conditional 필수 — when 조건이 true 일 때만 검증.
 * Yup.mixed().when(...) 의 then 에서 사용.
 *
 * 사용 예:
 *   bank_name_others: Yup.mixed().when("bank_name", {
 *       is: (bank) => extractValue(bank) === 'other',
 *       then: requiredField,
 *   })
 */

/**
 * 헬퍼 — value 가 object 면 .value 추출, 아니면 그대로 반환.
 * .when 의 is 콜백에서 활용 (문자열 비교용 — 'bca', 'other' 등).
 */
export const extractValue = (v) => (v && typeof v === 'object') ? v.value : v;

/**
 * 헬퍼 — 정수 비교용. 모바일은 native <select> 로 string ("1") 저장,
 * 데스크톱은 react-select 로 object {value:1, label:...} 저장하는 차이를 정규화.
 *
 *   {value:1, label:'X'} → 1
 *   "1"                  → 1
 *   1                    → 1
 *   null/undefined/""    → undefined
 *   "abc"                → undefined (parseInt 실패)
 */
export const extractIntValue = (v) => {
    if (v === null || v === undefined) return undefined;
    const raw = (typeof v === 'object') ? v.value : v;
    if (raw === null || raw === undefined || raw === '') return undefined;
    const n = parseInt(raw, 10);
    return isNaN(n) ? undefined : n;
};
